import React, { useState, useEffect } from 'react';
import { formFeedsApi, FormOption } from '../../../services/formFeedsApi';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

interface FeedFormProps {
  initialData?: {
    form_id: number;
    name: string;
    target_module: string;
    primary_key: string;
    on_submit: boolean;
    create_or_update: 'create' | 'update' | 'create_or_update';
    do_not_update_if_exists: boolean;
    is_active: boolean;
  };
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitButtonText?: string;
}

const FeedForm: React.FC<FeedFormProps> = ({ 
  initialData, 
  onSubmit, 
  onCancel, 
  isLoading = false,
  submitButtonText 
}) => {
  const [formOptions, setFormOptions] = useState<FormOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [formData, setFormData] = useState({
    form_id: initialData?.form_id || 0,
    name: initialData?.name || '',
    target_module: initialData?.target_module || 'project-requests',
    primary_key: initialData?.primary_key || 'project_name',
    on_submit: initialData?.on_submit ?? true,
    create_or_update: initialData?.create_or_update || 'create_or_update',
    do_not_update_if_exists: initialData?.do_not_update_if_exists ?? false,
    is_active: initialData?.is_active ?? true,
  });

  useEffect(() => {
    fetchFormOptions();
  }, []);

  const fetchFormOptions = async () => {
    try {
      const options = await formFeedsApi.getFormOptions();
      setFormOptions(options);
    } catch (error: any) {
      toast.error('Failed to load form options');
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.form_id) {
      toast.error('Please select a form');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Please enter a feed name');
      return;
    }
    if (!formData.primary_key.trim()) {
      toast.error('Please enter a primary key');
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full h-full">
      <div>
        <label className="block text-sm font-medium theme-text-primary mb-2">
          Form <span className="text-red-500">*</span>
        </label>
        <select
          name="form_id"
          value={formData.form_id}
          onChange={handleChange}
          disabled={loadingOptions || !!initialData}
          className="w-full px-4 py-2 rounded-lg theme-bg-tertiary theme-border border theme-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          required
        >
          <option value="">Select a form</option>
          {formOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.title}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium theme-text-primary mb-2">
          Feed Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Feed #1"
          className="w-full px-4 py-2 rounded-lg theme-bg-tertiary theme-border border theme-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium theme-text-primary mb-2">
          Target Module <span className="text-red-500">*</span>
        </label>
        <select
          name="target_module"
          value={formData.target_module}
          onChange={handleChange}
          className="w-full px-4 py-2 rounded-lg theme-bg-tertiary theme-border border theme-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        >
          <option value="project-requests">Project Requests</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium theme-text-primary mb-2">
          Primary Key <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="primary_key"
          value={formData.primary_key}
          onChange={handleChange}
          placeholder="e.g., project_name"
          className="w-full px-4 py-2 rounded-lg theme-bg-tertiary theme-border border theme-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium theme-text-primary mb-2">Create or Update</label>
        <select
          name="create_or_update"
          value={formData.create_or_update}
          onChange={handleChange}
          className="w-full px-4 py-2 rounded-lg theme-bg-tertiary theme-border border theme-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="create">Create Only</option>
          <option value="update">Update Only</option>
          <option value="create_or_update">Create or Update</option>
        </select>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="on_submit"
            checked={formData.on_submit}
            onChange={handleChange}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm theme-text-primary">Trigger on Submit</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="do_not_update_if_exists"
            checked={formData.do_not_update_if_exists}
            onChange={handleChange}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm theme-text-primary">Do Not Update If Exists</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm theme-text-primary">Active</span>
        </label>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitButtonText || (initialData ? 'Update Feed' : 'Create Feed')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-6 py-2.5 theme-bg-tertiary hover:theme-bg-quaternary theme-text-primary rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default FeedForm;
